import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { Cookie, CheckCircle2, Settings, BarChart3 } from "lucide-react";

const ELIDO_LOGO = "https://customer-assets.emergentagent.com/job_custom-demo-gen/artifacts/x2wfas84_Elido_logotype_blue.png";

export default function CookiesPage() {
  return (
    <div className="min-h-screen bg-white font-['Inter']">
      <Helmet>
        <title>Cookiepolicy | Elido AB - Så använder vi cookies</title>
        <meta name="description" content="Information om hur Elido AB använder cookies på elido.nu. Vi använder cookies för analys och för att förbättra din upplevelse." />
      </Helmet>

      {/* Header */}
      <header className="bg-[#0F2A30] py-4">
        <div className="max-w-4xl mx-auto px-6">
          <Link to="/">
            <img src={ELIDO_LOGO} alt="Elido" className="h-12 md:h-14" />
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="bg-[#0F2A30] py-12 lg:py-16">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-[#255E69] rounded-2xl mb-6">
            <Cookie className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Cookiepolicy
          </h1>
          <p className="text-lg text-gray-300 max-w-2xl mx-auto">
            Så använder vi cookies på elido.nu
          </p>
          <p className="text-sm text-gray-400 mt-4">
            Senast uppdaterad: september 2026
          </p>
        </div>
      </section>

      {/* Content */}
      <main className="py-12 lg:py-16">
        <div className="max-w-3xl mx-auto px-6">
          
          {/* Introduction */}
          <section className="mb-10">
            <p className="text-gray-600 text-lg leading-relaxed">
              Denna cookiepolicy förklarar vad cookies är, hur vi använder dem på elido.nu
              och vilka val du har. Nödvändig lagring används när den behövs för att webbplatsen
              och funktioner du begär ska fungera. Statistik och marknadsföring används enligt de
              val du gör i vår cookiebanner. Du kan ändra dina val när som helst via
              Cookieinställningar i sidfoten.
            </p>
          </section>

          {/* What are cookies */}
          <section className="mb-10">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Vad är cookies?</h2>
            <p className="text-gray-600 leading-relaxed">
              Cookies är små textfiler som lagras på din enhet när du besöker en webbplats. 
              De hjälper webbplatsen att komma ihåg information om ditt besök, som dina 
              inställningar och hur du navigerar på sidan.
            </p>
          </section>

          {/* Types of cookies */}
          <section className="mb-10">
            <h2 className="text-xl font-bold text-gray-900 mb-6">Cookies vi använder</h2>
            
            <div className="space-y-4">
              {/* Necessary cookies */}
              <div className="bg-gray-50 p-5 rounded-xl">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                    <CheckCircle2 className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">Nödvändiga</h3>
                    <span className="text-xs text-green-600 font-medium">Alltid aktiva</span>
                  </div>
                </div>
                <p className="text-gray-600 text-sm">
                  Behövs för webbplatsens grundläggande funktioner och för att komma ihåg ditt
                  cookieval.
                </p>
              </div>

              {/* Statistics cookies */}
              <div className="bg-gray-50 p-5 rounded-xl">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                    <BarChart3 className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">Statistik</h3>
                    <span className="text-xs text-blue-600 font-medium">Kräver ditt val</span>
                  </div>
                </div>
                <p className="text-gray-600 text-sm">
                  Används för att förstå hur webbplatsen används och mäta exempelvis sidvisningar
                  och interaktioner. Elido använder Google Analytics (GA4) för webbanalys.
                </p>
              </div>

              {/* Marketing cookies */}
              <div className="bg-gray-50 p-5 rounded-xl">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                    <Settings className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">Marknadsföring</h3>
                    <span className="text-xs text-purple-600 font-medium">Kräver ditt val</span>
                  </div>
                </div>
                <p className="text-gray-600 text-sm">
                  Används för att mäta annonsering och marknadsföring. Elido använder Google Ads
                  och Meta Pixel när du har godkänt marknadsföring.
                </p>
              </div>
            </div>
          </section>

          {/* How to manage */}
          <section className="mb-10">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Hantera cookies</h2>
            <p className="text-gray-600 leading-relaxed mb-4">
              Du kan när som helst öppna Cookieinställningar via länken i sidfoten och ändra eller
              återkalla ditt val. Du kan även radera lagrad webbplatsdata via din webbläsare.
            </p>
          </section>

          {/* Third parties */}
          <section className="mb-10">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Tredjepartstjänster</h2>
            <p className="text-gray-600 leading-relaxed">
              Vi använder följande tredjepartstjänster. Statistik respektive marknadsföring styrs
              av dina val i vår cookiebanner:
            </p>
            <ul className="mt-4 space-y-2 text-gray-600">
              <li className="flex items-center gap-2">
                <span className="w-2 h-2 bg-[#255E69] rounded-full"></span>
                <strong>Google Analytics</strong> – statistik
              </li>
              <li className="flex items-center gap-2">
                <span className="w-2 h-2 bg-[#255E69] rounded-full"></span>
                <strong>Google Ads</strong> – annonseringsmätning
              </li>
              <li className="flex items-center gap-2">
                <span className="w-2 h-2 bg-[#255E69] rounded-full"></span>
                <strong>Meta Pixel</strong> – annonseringsmätning
              </li>
            </ul>
          </section>

          {/* Contact & More info */}
          <section className="mb-10">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Mer information</h2>
            <p className="text-gray-600 leading-relaxed">
              För mer information om hur vi hanterar dina personuppgifter, läs vår{" "}
              <Link to="/integritetspolicy" className="text-[#255E69] hover:underline font-medium">
                integritetspolicy
              </Link>
              .
            </p>
            <p className="text-gray-600 leading-relaxed mt-4">
              Har du frågor om vår användning av cookies? Kontakta oss på{" "}
              <a href="mailto:hej@elido.nu" className="text-[#255E69] hover:underline font-medium">
                hej@elido.nu
              </a>
              .
            </p>
          </section>

          {/* Back link */}
          <div className="border-t border-gray-100 pt-8 mt-12">
            <Link 
              to="/"
              className="text-[#255E69] hover:underline font-medium inline-flex items-center gap-2"
            >
              ← Tillbaka till startsidan
            </Link>
          </div>

        </div>
      </main>

      {/* Footer */}
      <footer className="bg-[#0F2A30] py-8">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <p className="text-gray-500 text-sm">
            © {new Date().getFullYear()} Elido AB. Alla rättigheter förbehållna.
          </p>
          <p className="text-gray-600 text-xs mt-1">
            Egenutvecklad plattform optimerad för att generera fler kunder
          </p>
        </div>
      </footer>
    </div>
  );
}
